import { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { authCache } from 'api/cache';
import { UserProfile, UserTenantRolesMap } from 'api/endpoints/users';
import { useCurrentTenant } from 'api/tenantCache';
import {
  ReadingCacheType,
  useReadAPIWithServices,
  useWriteAPIWithServices,
} from 'api/useAPIWithServices';
import ROUTES from 'app/routes/paths';
import { SYSTEM_ROLE_FULL, TENANT_ROLE_FULL, UserRole } from 'constants/roles';
import { useAppServices } from 'container/depContext';
import { useUserInfo } from 'hooks/useUserInfo';
import { string } from 'utils/formatters';

import { ProfileMenuProps } from './types';

type HandleConfirmLogOut = EmptyArgsVoidFunc;
type HandleChangeTenant = (selected: SelectItem) => void;
type HandleSetAction = (type: string) => () => void;
type HandleResetActiveAction = EmptyArgsVoidFunc;
const useProfileMenu = (onItemClick: ProfileMenuProps['onItemClick']) => {
  const { push, go, location } = useHistory();

  const [activeAction, setActiveAction] =
    useState<Nullable<ActiveAction<undefined>>>(null);

  const { getUserRepo, getTenantsRepo } = useAppServices();
  const { getCurrentUserInfo, updateCurrentUserActiveTenant } = getUserRepo();
  const { getTenants } = getTenantsRepo();

  const { currentUserInfo: info, isCurrentUserInfoLoading: isInfoLoading } =
    useUserInfo();

  const { mutate: refetchInfo, isLoading: isInfoValidating } =
    useReadAPIWithServices([getCurrentUserInfo.name], getCurrentUserInfo, {
      cacheType: ReadingCacheType.Static,
    });

  const {
    trigger: updateCurrentUserActiveTenantTrigger,
    isExecuting: isUpdateCurrentUserActiveTenantExecuting,
  } = useWriteAPIWithServices<string, UserProfile>(
    [updateCurrentUserActiveTenant.name],
    (tenantId) => updateCurrentUserActiveTenant(tenantId)
  );

  const { data: tenants, isLoading: areTenantsLoading } =
    useReadAPIWithServices([getTenants.name], getTenants, {
      cacheType: ReadingCacheType.Static,
    });

  const userTenantRoles: UserTenantRolesMap = Object.fromEntries(
    (info?.tenants || []).map((t) => [t.tenant.id, t.userRole])
  );

  const userCurrentTenant: BaseEntity | undefined = useCurrentTenant();

  const currentTenantRole = (() => {
    const role = info?.role;
    if (!role) return 'Loading...';

    if (role === UserRole.User) {
      const tid = userCurrentTenant?.id;
      if (tid === undefined) return 'User';

      return TENANT_ROLE_FULL[userTenantRoles[tid]];
    }

    return SYSTEM_ROLE_FULL[role];
  })();

  const activeTenant: SelectItem | undefined = userCurrentTenant
    ? {
        label: userCurrentTenant.name,
        value: userCurrentTenant.id,
      }
    : undefined;

  const normalizedTenants: SelectItem[] = tenants?.data
    ? tenants.data.map((e) => ({
        label: e.name,
        value: e.id,
      }))
    : [];

  const [isLogOutPending, setIsLogOutPending] = useState(false);
  const handleConfirmLogOut: HandleConfirmLogOut = () =>
    void (async () => {
      setIsLogOutPending(true);
      await authCache.logout();
      document.location.reload();
    })();

  const anyPending: boolean =
    isInfoLoading ||
    isUpdateCurrentUserActiveTenantExecuting ||
    isInfoValidating ||
    areTenantsLoading;

  const handleChangeTenant: HandleChangeTenant = (selected) =>
    void (async () => {
      if (selected.value === activeTenant?.value) return;

      try {
        await updateCurrentUserActiveTenantTrigger(selected.value);

        if (location.pathname === ROUTES.process.execution._()) return go(0); // go(0) is equivelant to location.reload() so we'll abort instead of doing anything else that'll fail

        push(string.withDefaultPagination(ROUTES.process.execution._()));
        await refetchInfo();
        onItemClick?.();
      } catch {
        return;
      }
    })();

  const handleSetAction: HandleSetAction = (type) => () => {
    setActiveAction({ type });
  };

  const handleResetActiveAction: HandleResetActiveAction = () => {
    setActiveAction(null);
  };

  return {
    handleConfirmLogOut,
    isLogOutPending,
    info,
    currentTenantRole,
    normalizedTenants,
    handleChangeTenant,
    activeTenant,
    anyPending,
    handleSetAction,
    activeAction,
    handleResetActiveAction,
  };
};

export default useProfileMenu;
